import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Phaser from 'phaser';
import { AREAS, type MeDto } from '@bb/shared';
import { AreaScene, type AreaEvents } from '../game/AreaScene';
import { api } from '../net/api';
import { wearFrom, type AvatarLook } from '../art/avatar';
import { Shop } from './Shop';
import { Backpack } from './Backpack';
import { Fishing } from './Fishing';
import { HomeEditor } from './HomeEditor';
import { Farm } from './Farm';
import { Mine } from './Mine';
import { Messages } from './Messages';
import { ChatBar, type ChatLine } from './ChatBar';
import { ProfileCard } from './ProfileCard';
import { Friends } from './Friends';
import { Arcade } from './Arcade';
import { Journal } from './Journal';
import { Account } from './Account';
import { NoticeBoard } from './NoticeBoard';
import { BugReport, snapshotGame } from './BugReport';
import { DailyGift, QuestCard } from './Progression';
import { GiftBox, SendGift } from './Gifts';
import { SoundMenu } from './SoundMenu';
import { play, playMusic, type MusicTheme } from '../audio/sound';
import { events, family, gifts, messages, progression, ApiError } from '../net/api';
import type { ProgressNews } from '../net/api';

type Status = Parameters<AreaEvents['onStatus']>[0];
type Panel =
  | { kind: 'shop'; key: string }
  | { kind: 'backpack' }
  | { kind: 'fishing' }
  | { kind: 'mine' }
  | { kind: 'friends' }
  | { kind: 'messages' }
  | { kind: 'profile'; name: string }
  | { kind: 'arcade'; mode?: 'halloween'; joinSquad?: string }
  | { kind: 'journal' }
  | { kind: 'account' }
  | { kind: 'notice' }
  | { kind: 'bug'; screenshot?: string; from?: string }
  | { kind: 'daily' }
  | { kind: 'giftbox' }
  | { kind: 'gift'; to: string }
  | null;
type Catalog = Map<string, { render: Record<string, unknown> }>;

export function Play({ me: initialMe, onSignOut, onReconnect, onNewCharacter }: { me: MeDto; onSignOut: () => void; onReconnect: () => void; onNewCharacter: () => void }) {
  const [me, setMe] = useState(initialMe);
  const [area, setArea] = useState<string>('town');
  const [atFarm, setAtFarm] = useState(false);
  const [panel, setPanel] = useState<Panel>(null);
  const [catalog, setCatalog] = useState<Catalog>();
  const [status, setStatus] = useState<Status>({ kind: 'connecting' });
  const [toast, setToast] = useState<string>();
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [sender, setSender] = useState<((type: string, payload: unknown) => void) | null>(null);
  const showToast = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast((t) => (t === text ? undefined : t)), 3500);
  }, []);
  const onChat = useCallback((l: Omit<ChatLine, 'at'>) => setLines((ls) => [...ls.slice(-49), { ...l, at: Date.now() }]), []);
  const onPlayer = useCallback((name: string) => setPanel({ kind: 'profile', name }), []);
  const bindSender = useCallback((fn: ((type: string, payload: unknown) => void) | null) => setSender(() => fn), []);
  const onNotice = useCallback((m: string) => showToast(m), [showToast]);
  const [invite, setInvite] = useState<{ roomId: string; from: string }>();
  const onSquadInvite = useCallback((i: { roomId: string; from: string }) => {
    setInvite(i);
    // Invites expire with the room, so the banner doesn't hang around.
    window.setTimeout(() => setInvite((cur) => (cur?.roomId === i.roomId ? undefined : cur)), 60_000);
  }, []);
  const onGhost = useCallback(
    (g: { caught: boolean; candy: number; found: number; total: number }) => {
      if (!g.caught) return;
      showToast(g.found >= g.total ? `You caught every ghost! +${g.candy} candy` : `Boo! You caught a ghost: +${g.candy} candy (${g.found} of ${g.total})`);
      void api.me().then(setMe, () => {});
    },
    [showToast],
  );
  const host = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | undefined>(undefined);

  // Count play time once a minute (for everyone); supervised players stop when a parent's limit is reached.
  const [locked, setLocked] = useState<string>();
  useEffect(() => {
    // Everyone's play time counts (parents' limits use it, and so does automatic verification).
    let warned = false;
    const ping = () =>
      family.activity().then(
        (r) => {
          if (r.minutesLeft !== null && r.minutesLeft <= 5 && r.minutesLeft > 0 && !warned) {
            warned = true;
            showToast(`${r.minutesLeft} minute${r.minutesLeft === 1 ? '' : 's'} of play time left today.`);
          }
        },
        (e) => {
          if (e instanceof ApiError && e.code === 'play_locked') setLocked(e.message);
        },
      );
    void ping();
    const id = window.setInterval(ping, 60_000);
    return () => window.clearInterval(id);
  }, [me.family?.supervised, showToast]);

  const [decor, setDecor] = useState<string[]>();
  const [liveEvents, setLiveEvents] = useState<string[]>([]);
  useEffect(() => {
    // Only the first answer counts (dev StrictMode fetches twice); a new array would rebuild the game.
    events.list().then(
      (r) => {
        setDecor((d) => d ?? r.events.filter((e) => e.live && e.theme.decor).map((e) => e.theme.decor!));
        setLiveEvents(r.events.filter((e) => e.live).map((e) => e.key));
      },
      () => setDecor((d) => d ?? []),
    );
  }, []);

  useEffect(() => {
    api.catalog().then((r) => setCatalog((c) => c ?? new Map(r.items.map((i) => [i.key, i]))), () => setCatalog((c) => c ?? new Map()));
  }, []);

  const [celebrations, setCelebrations] = useState<string[]>([]);
  // Bumped after anything that might tick a welcome-guide step, so the checklist reloads.
  const [questVersion, setQuestVersion] = useState(0);
  // Bumped when a shop closes so the home editor reloads newly bought furniture.
  const [homeVersion, setHomeVersion] = useState(0);
  const celebrate = useCallback((news?: ProgressNews) => {
    if (!news) return;
    const r = (x: { coins?: number; xp?: number; candy?: number }) =>
      [x.coins ? `+${x.coins} coins` : '', x.candy ? `+${x.candy} candy` : '', x.xp ? `+${x.xp} XP` : ''].filter(Boolean).join(', ');
    const lines = [
      ...news.achievements.map((a) => `Achievement unlocked: ${a.name} (${r(a.reward)})`),
      ...news.challenges.map((c) => `Challenge complete: ${c.name} (${r(c.reward)})`),
      ...news.collections.map((c) => `Collection complete: ${c.name} (${r(c.reward)})`),
      ...(news.quest ?? []).map((q) =>
        q.finished ? `Welcome guide complete! ${r(q.reward)}${q.reward.item ? ` and the ${q.reward.item}` : ''}` : `Welcome guide: ${q.name} ✓ (${r(q.reward)})`,
      ),
    ];
    if (news.quest?.length) setQuestVersion((v) => v + 1);
    if (!lines.length) return;
    play('chime');
    setCelebrations((cs) => [...cs, ...lines]);
    window.setTimeout(() => setCelebrations((cs) => cs.slice(lines.length)), 5000);
  }, []);
  const refresh = useCallback((news?: ProgressNews) => {
    celebrate(news);
    void api.me().then(setMe, () => {});
  }, [celebrate]);
  useEffect(() => refresh(), [refresh]);

  // Daily gift: for returning players it opens by itself while today's gift is waiting.
  // Brand-new players get the welcome guide first and just see the highlighted button.
  const [dailyWaiting, setDailyWaiting] = useState(false);
  useEffect(() => {
    progression.daily().then((d) => {
      setDailyWaiting(!d.claimedToday);
      if (!d.claimedToday && d.totalClaims > 0) setPanel((p) => p ?? { kind: 'daily' });
    }, () => {});
  }, []);

  // Private messages and group invites waiting (checked every 30 seconds).
  const [unreadMessages, setUnreadMessages] = useState(0);
  const checkMessages = useCallback(() => void messages.unread().then((r) => setUnreadMessages(r.unread + r.invites), () => {}), []);
  useEffect(() => {
    checkMessages();
    const id = window.setInterval(checkMessages, 30_000);
    return () => window.clearInterval(id);
  }, [checkMessages]);

  // Gifts waiting in the gift box (checked now and then; opening the box refreshes it).
  const [giftCount, setGiftCount] = useState(0);
  const checkGifts = useCallback(() => void gifts.box().then((b) => setGiftCount(b.waiting.length), () => {}), []);
  useEffect(() => {
    checkGifts();
    const id = window.setInterval(checkGifts, 60_000);
    return () => window.clearInterval(id);
  }, [checkGifts]);

  // Closing any panel may have finished a guide step (bought something, sent a friend request…).
  const closePanel = useCallback(() => {
    setPanel(null);
    setQuestVersion((v) => v + 1);
  }, []);

  const onHotspot = useCallback((action: string, label: string) => {
    const [kind, arg] = action.split(':');
    if (kind === 'area' && arg) setArea(arg);
    else if (kind === 'shop' && arg) setPanel({ kind: 'shop', key: arg });
    else if (kind === 'minigame' && arg === 'fishing') setPanel({ kind: 'fishing' });
    else if (kind === 'home') setArea('home');
    else if (kind === 'arcade') setPanel({ kind: 'arcade', mode: arg === 'halloween' ? 'halloween' : undefined });
    else if (kind === 'notice') setPanel({ kind: 'notice' });
    else if (kind === 'mine') setPanel({ kind: 'mine' });
    else showToast(`${label} opens soon.`);
  }, [showToast]);

  // One Phaser game per public area; switching areas leaves the old room.
  useEffect(() => {
    if (!host.current || !catalog || !decor || area === 'home') return;
    // ?renderer=canvas forces the 2D canvas renderer (useful on machines without a GPU).
    const forceCanvas = new URLSearchParams(window.location.search).get('renderer') === 'canvas';
    const parent = host.current;
    let game: Phaser.Game | undefined;
    // Created on the next tick so an immediately-cancelled mount (React StrictMode,
    // fast area changes) never boots a game whose canvas could linger.
    const timer = window.setTimeout(() => {
      game = new Phaser.Game({
        type: forceCanvas ? Phaser.CANVAS : Phaser.AUTO,
        parent,
        width: AREAS[area]!.width,
        height: AREAS[area]!.height,
        backgroundColor: '#8fcf7a',
        scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
        render: { antialias: true },
        // Only clicks on the game canvas count; otherwise clicks inside panels (shops, the arcade)
        // also reach avatars and hotspots underneath.
        input: { windowEvents: false },
        banner: false,
      });
      gameRef.current = game;
      game.scene.add('area', AreaScene, true, { area, decor, liveEvents, catalog, onStatus: setStatus, onHotspot, onChat, onNotice, onPlayer, bindSender, onProgress: celebrate, onGhost, onSquadInvite } satisfies AreaEvents);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      if (gameRef.current === game) gameRef.current = undefined;
      game?.destroy(true);
    };
  }, [area, catalog, decor, liveEvents, onHotspot, onChat, onNotice, onPlayer, bindSender, celebrate, onGhost, onSquadInvite]);

  const look: AvatarLook = useMemo(
    () => ({
      characterType: me.character?.characterType ?? 'creature',
      species: me.character?.species ?? null,
      appearance: me.character?.appearance ?? {},
      wear: catalog ? wearFrom(me.character?.equipped ?? {}, catalog) : {},
    }),
    [me, catalog],
  );

  // The picture is taken before the panel opens, so it shows what the player was looking at.
  const openBugReport = useCallback(async () => {
    const from = panel?.kind;
    const screenshot = area === 'home' ? undefined : await snapshotGame(gameRef.current);
    setPanel({ kind: 'bug', screenshot, from });
  }, [area, panel]);

  // Music follows the area; coins and level-ups get a sound wherever they came from.
  useEffect(() => {
    const theme: MusicTheme = area === 'home' ? 'home' : area === 'beach' ? 'beach' : area === 'haunted' ? 'haunted' : 'town';
    playMusic(theme);
  }, [area]);
  useEffect(() => () => playMusic('none'), []);
  const lastWallet = useRef<{ coins: number; level: number } | undefined>(undefined);
  useEffect(() => {
    const now = { coins: me.wallet?.COINS ?? 0, level: me.progress?.level ?? 1 };
    const before = lastWallet.current;
    lastWallet.current = now;
    if (!before) return;
    if (now.level > before.level) play('levelUp');
    else if (now.coins > before.coins) play('coin');
  }, [me]);

  const coins = me.wallet?.COINS ?? 0;
  const p = me.progress;
  const pct = p && p.nextLevelXp ? Math.round(((p.xp - p.levelXp) / (p.nextLevelXp - p.levelXp)) * 100) : 100;
  const areaName = area === 'home' ? (atFarm ? 'Farm' : 'Cove Cottage') : AREAS[area]?.name;

  return (
    <div className="play">
      <header className="topbar">
        <span className="brand small">Buddy Bay</span>
        <span className="place">{areaName}</span>
        {area !== 'home' && (
          <span className="muted">{status.kind === 'online' ? `${status.count ?? 1} here` : status.kind === 'connecting' ? 'Connecting…' : 'Offline'}</span>
        )}
        <span className="spacer" />
        <span className="hud-coins" title="Coins">{coins.toLocaleString()} coins</span>
        {((me.wallet?.CANDY ?? 0) > 0 || liveEvents.includes('haunted_harbor')) && (
          <span className="hud-coins candy" title="Candy (Halloween)">{(me.wallet?.CANDY ?? 0).toLocaleString()} candy</span>
        )}
        {(me.wallet?.PEARLS ?? 0) > 0 && <span className="hud-coins pearls" title="Pearls">{(me.wallet?.PEARLS ?? 0).toLocaleString()} pearls</span>}
        {me.memberUntil && <span className="tag member" title={`Member until ${new Date(me.memberUntil).toLocaleDateString()}`}>Member</span>}
        {p && (
          <span className="hud-level" title={`${p.xp} XP${p.nextLevelXp ? ` · ${p.nextLevelXp - p.xp} to next level` : ''}`}>
            Level {p.level}
            <span className="xpbar" aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>
          </span>
        )}
        <button className="btn ghost" onClick={() => setPanel({ kind: 'backpack' })}>Backpack</button>
        <button className="btn ghost" onClick={() => setPanel({ kind: 'friends' })}>Friends</button>
        <button className="btn ghost" onClick={() => setPanel({ kind: 'journal' })}>Journal</button>
        <button className="btn ghost" onClick={() => setPanel({ kind: 'messages' })} aria-label={unreadMessages ? `Messages, ${unreadMessages} new` : 'Messages'}>
          Messages{unreadMessages > 0 && <span className="badge">{unreadMessages}</span>}
        </button>
        <button className="who link-btn" onClick={() => setPanel({ kind: 'account' })} title="Your account">{me.character?.displayName}</button>
        <button className="btn ghost account-btn" onClick={() => setPanel({ kind: 'account' })}>Account</button>
        {dailyWaiting && <button className="btn ghost daily-btn" onClick={() => setPanel({ kind: 'daily' })}>Daily gift</button>}
        <button className="btn ghost icon-btn gift-btn" onClick={() => setPanel({ kind: 'giftbox' })} title="Gift box" aria-label={giftCount ? `Gift box, ${giftCount} new` : 'Gift box'}>
          🎁{giftCount > 0 && <span className="badge">{giftCount}</span>}
        </button>
        <SoundMenu />
        <button className="btn ghost icon-btn" onClick={() => void openBugReport()} title="Found a bug? Tell us" aria-label="Tell us about a bug">🐞</button>
        <button className="btn ghost" onClick={onSignOut}>Sign out</button>
      </header>

      {area === 'home' && atFarm ? (
        <Farm ownerName={me.character?.displayName ?? 'Your'} onBack={() => { setAtFarm(false); setHomeVersion((v) => v + 1); }} onChanged={refresh} />
      ) : area === 'home' ? (
        <HomeEditor
          key={homeVersion}
          ownerName={me.character?.displayName ?? 'Your'}
          onLeave={() => setArea('town')}
          onShop={() => setPanel({ kind: 'shop', key: 'driftwood' })}
          onFarm={() => setAtFarm(true)}
        />
      ) : (
        <div className="stage" ref={host} key={area} />
      )}

      {panel?.kind === 'shop' && (
        <Shop
          shopKey={panel.key}
          look={look}
          onClose={() => {
            closePanel();
            setHomeVersion((v) => v + 1);
          }}
          onChanged={refresh}
        />
      )}
      {panel?.kind === 'backpack' && (
        <Backpack look={look} equipped={(me.character?.equipped ?? {}) as Record<string, string>} onClose={closePanel} onChanged={refresh} />
      )}
      {panel?.kind === 'fishing' && <Fishing onClose={closePanel} onChanged={refresh} />}
      {panel?.kind === 'mine' && <Mine look={look} onClose={closePanel} onChanged={refresh} />}
      {panel?.kind === 'messages' && <Messages onClose={() => { closePanel(); checkMessages(); }} onRead={checkMessages} />}
      {panel?.kind === 'friends' && catalog && <Friends catalog={catalog} onClose={closePanel} onProfile={(name) => setPanel({ kind: 'profile', name })} onGift={(to) => setPanel({ kind: 'gift', to })} />}
      {panel?.kind === 'gift' && <SendGift to={panel.to} look={look} onBack={() => setPanel({ kind: 'friends' })} onClose={closePanel} onSent={refresh} />}
      {panel?.kind === 'giftbox' && <GiftBox look={look} onClose={() => { closePanel(); checkGifts(); }} onChanged={(n) => { refresh(n); checkGifts(); }} />}
      {panel?.kind === 'daily' && <DailyGift onClose={closePanel} onClaimed={(n) => { setDailyWaiting(false); refresh(n); }} />}
      {panel?.kind === 'profile' && catalog && <ProfileCard name={panel.name} catalog={catalog} onClose={closePanel} />}
      {panel?.kind === 'arcade' && <Arcade mode={panel.mode} brand={me.squadBrand} joinSquad={panel.joinSquad} onClose={closePanel} onChanged={refresh} />}
      {panel?.kind === 'notice' && <NoticeBoard onClose={closePanel} onShop={(key) => setPanel({ kind: 'shop', key })} />}
      {panel?.kind === 'account' && <Account username={me.user.username} emailVerified={me.emailVerified} canBeParent={me.canBeParent} supervised={me.family?.supervised} isStaff={me.user.isStaff} onNewCharacter={onNewCharacter} onClose={closePanel} />}
      {panel?.kind === 'journal' && <Journal onClose={closePanel} me={me.character?.displayName ?? ''} look={look} brand={me.squadBrand} />}
      {panel?.kind === 'bug' && <BugReport area={area === 'home' ? (atFarm ? 'farm' : 'home') : area} panel={panel.from} screenshot={panel.screenshot} onClose={closePanel} />}
      {<QuestCard version={questVersion} compact={area === 'home'} onChanged={() => setQuestVersion((v) => v + 1)} />}
      {area !== 'home' && <ChatBar send={sender} lines={lines} />}
      {celebrations.length > 0 && (
        <div className="celebrations" role="status">
          {celebrations.map((c, i) => <p key={i} className="celebration">{c}</p>)}
        </div>
      )}

      {status.kind === 'closed' && area !== 'home' && (
        <div className="overlay" role="alertdialog" aria-labelledby="closed-title">
          <div className="panel narrow">
            <h2 id="closed-title">Disconnected</h2>
            <p>{status.message}</p>
            <button className="btn" onClick={onReconnect}>Rejoin</button>
          </div>
        </div>
      )}
      {toast && <p className="toast" role="status">{toast}</p>}
      {invite && (
        <div className="invite-banner" role="alertdialog" aria-label="Squad invite">
          <span><strong>{invite.from}</strong> invited you to play {me.squadBrand === 'bubble' ? 'Bubble Squad' : 'Bomb Squad'} together!</span>
          <button className="btn small" onClick={() => { setPanel({ kind: 'arcade', joinSquad: invite.roomId }); setInvite(undefined); }}>Join</button>
          <button className="btn small ghost" onClick={() => setInvite(undefined)}>No thanks</button>
        </div>
      )}
      {locked && (
        <div className="overlay" role="alertdialog" aria-labelledby="locked-title">
          <div className="panel narrow">
            <h2 id="locked-title">Time for a break!</h2>
            <p>{locked}</p>
            <button className="btn" onClick={onSignOut}>Sign out</button>
          </div>
        </div>
      )}

    </div>
  );
}
